/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  MICRO_FADE_DURATION_MS,
  LONG_FADE_DURATION_MS,
  FADE_IN_CURVE,
  FADE_OUT_CURVE,
} from "PhonosConstants";
import { ClipEnhancementInfo, ClipId, PlaylistClip } from "types/Clip";
import AudioBufferCache from "audioProcessing/AudioBufferCache";
import { millisecondsToSeconds, secondsToMilliseconds } from "TimeHelper";
import { getTimeAlterations } from "utilities/ClipUtilities";

export function buildNodes(
  context: BaseAudioContext,
  playlist: PlaylistClip[],
  cache: AudioBufferCache,
  seekTime = 0
): Promise<PlaylistItemNode>[] {
  return playlist
    .filter((item) => clipCanQueueAtTime(item, seekTime))
    .map((item) => queueItem(context, cache, item, seekTime));
}

const clipCanQueueAtTime = (item: PlaylistClip, seekTime: number): boolean => {
  return item.duration > 0 && item.startTime + item.duration > seekTime;
};

export enum GainNodeType {
  fadeIn,
  fadeOut,
  background,
  crossFadeOut,
  crossFadeIn,
  originalSource,
  enhanceSource,
  mix,
}

export interface TypedGainNode {
  node: AudioNode;
  type: GainNodeType;
}

export interface PlaylistItemNode {
  clipId: ClipId;
  sourceNodePair: AudioBufferSourceNodePair;
  gains: TypedGainNode[];
}

async function queueItem(
  context: BaseAudioContext,
  cache: AudioBufferCache,
  item: PlaylistClip,
  seekTime: number
): Promise<PlaylistItemNode> {
  const sourceNodePair = await buildSourcesForItem(context, cache, item);
  const { mixGains, output: mixedSource } = mixSources(
    context,
    sourceNodePair,
    item.enhancementInfo
  );

  // For items playing in the future, item.startTime - seekTime will always be positive.
  // For items "partially" in the past (related to context.currentTime), we must clamp startTime to 0 to start playback now
  const when =
    secondsToMilliseconds(context.currentTime) +
    Math.max(item.startTime - seekTime, 0);
  const seekOffset = Math.max(seekTime - item.startTime, 0);
  const offset = Math.max(item.offset + seekOffset, 0);
  const duration = Math.max(
    Math.min(item.startTime + item.duration - seekTime, item.duration),
    0
  );

  // TODO: Separate building and connecting source->gains->audioContext?
  const effectGains = buildAndConnectGains(
    context,
    item,
    mixedSource,
    when,
    offset,
    duration,
    seekOffset
  );

  console.debug(`Queue original: ${item.assetId}`);
  console.debug(`Queue enhanced: ${item.enhancementInfo?.assetId}`);

  sourceNodePair.original.start(
    millisecondsToSeconds(when),
    millisecondsToSeconds(offset),
    duration === Number.POSITIVE_INFINITY
      ? undefined
      : millisecondsToSeconds(duration)
  );

  sourceNodePair.enhanced?.start(
    millisecondsToSeconds(when),
    millisecondsToSeconds(offset),
    duration === Number.POSITIVE_INFINITY
      ? undefined
      : millisecondsToSeconds(duration)
  );

  return {
    clipId: item.id,
    sourceNodePair,
    gains: [...mixGains, ...effectGains],
  };
}

interface AudioBufferSourceNodePair {
  original: AudioBufferSourceNode;
  enhanced?: AudioBufferSourceNode;
}

async function buildSourcesForItem(
  context: BaseAudioContext,
  cache: AudioBufferCache,
  item: PlaylistClip
): Promise<AudioBufferSourceNodePair> {
  const originalNode = context.createBufferSource();

  const originalAssetAudioBuffer = await cache.getAssetAudioBuffer(
    item.assetId
  );

  if (originalAssetAudioBuffer !== undefined) {
    originalNode.buffer = await originalAssetAudioBuffer;
  } else {
    console.warn("unable to queue playlist item", item.id);
    // TODO: Send NR error?
  }

  const sourceNodePair: AudioBufferSourceNodePair = { original: originalNode };

  if (item.enhancementInfo) {
    const enhancedNode = context.createBufferSource();

    const enhancedAssetAudioBuffer = await cache.getAssetAudioBuffer(
      item.enhancementInfo.assetId
    );

    if (enhancedAssetAudioBuffer !== undefined) {
      enhancedNode.buffer = enhancedAssetAudioBuffer;
      sourceNodePair.enhanced = enhancedNode;
    } else {
      console.warn("unable to queue playlist item", item.id);
      // TODO: Send NR error?
    }
  }

  return sourceNodePair;
}

const mixSources = (
  context: BaseAudioContext,
  sources: AudioBufferSourceNodePair,
  enhancementInfo: ClipEnhancementInfo | undefined
): { mixGains: TypedGainNode[]; output: AudioNode } => {
  if (!sources.enhanced || !enhancementInfo) {
    return { mixGains: [], output: sources.original };
  }

  const originalGain = context.createGain();
  sources.original.connect(originalGain);

  const enhancedGain = context.createGain();
  sources.enhanced.connect(enhancedGain);

  const mixNode = context.createGain();
  originalGain.connect(mixNode);
  enhancedGain.connect(mixNode);

  originalGain.gain.value = 1 - enhancementInfo.strengthPercent / 100;
  enhancedGain.gain.value = enhancementInfo.strengthPercent / 100;

  return {
    output: mixNode,
    mixGains: [
      {
        node: originalGain,
        type: GainNodeType.originalSource,
      },
      {
        node: enhancedGain,
        type: GainNodeType.enhanceSource,
      },
      { node: mixNode, type: GainNodeType.mix },
    ],
  };
};

function buildAndConnectGains(
  context: BaseAudioContext,
  item: PlaylistClip,
  source: AudioNode,
  when: number,
  offset: number,
  duration: number,
  seekOffset: number
): TypedGainNode[] {
  let lastSource = source;
  const gainNodes: TypedGainNode[] = [];

  const itemTimeAlterations = getTimeAlterations(item);
  if (itemTimeAlterations.isBackground) {
    const backgroundGain = buildBackgroundGainNode(
      context,
      itemTimeAlterations.backgroundGain
    );
    lastSource.connect(backgroundGain);
    lastSource = backgroundGain;
    gainNodes.push({
      node: backgroundGain,
      type: GainNodeType.background,
    });
  }

  if (itemTimeAlterations.fadeIn) {
    const fadeIn = buildFadeInGain(
      context,
      when,
      seekOffset,
      LONG_FADE_DURATION_MS
    );
    if (fadeIn) {
      lastSource.connect(fadeIn);
      lastSource = fadeIn;
      gainNodes.push({
        node: fadeIn,
        type: GainNodeType.fadeIn,
      });
    }
  }

  if (itemTimeAlterations.fadeOut) {
    const fadeOut = buildFadeOutGain(
      context,
      when,
      duration,
      LONG_FADE_DURATION_MS
    );
    lastSource.connect(fadeOut);
    lastSource = fadeOut;
    gainNodes.push({
      node: fadeOut,
      type: GainNodeType.fadeOut,
    });
  }

  const crossFadeIn = buildFadeInGain(
    context,
    when,
    seekOffset,
    MICRO_FADE_DURATION_MS
  );
  if (crossFadeIn) {
    lastSource.connect(crossFadeIn);
    lastSource = crossFadeIn;
    gainNodes.push({
      node: crossFadeIn,
      type: GainNodeType.crossFadeIn,
    });
  }

  if (duration !== Number.POSITIVE_INFINITY) {
    const crossFadeOut = buildFadeOutGain(
      context,
      when,
      duration,
      MICRO_FADE_DURATION_MS
    );
    lastSource.connect(crossFadeOut);
    lastSource = crossFadeOut;
    gainNodes.push({
      node: crossFadeOut,
      type: GainNodeType.crossFadeOut,
    });
  }

  lastSource.connect(context.destination);

  return gainNodes;
}
const DEFAULT_BACKGROUND_GAIN = 0.1;

function buildBackgroundGainNode(
  context: BaseAudioContext,
  value: number = DEFAULT_BACKGROUND_GAIN
): AudioNode {
  const node = context.createGain();
  node.gain.setValueAtTime(value, millisecondsToSeconds(context.currentTime));
  return node;
}

export const updateBackgroundGainNode = (
  context: BaseAudioContext,
  node: GainNode,
  value: number = DEFAULT_BACKGROUND_GAIN
): void => {
  node.gain.setValueAtTime(value, context.currentTime);
};

function buildFadeInGain(
  context: BaseAudioContext,
  when: number,
  seekOffset: number,
  fadeDuration: number
): AudioNode | undefined {
  // Skip if the playhead is after the fade we're adding
  if (seekOffset >= fadeDuration) {
    return;
  }

  const curveDuration = fadeDuration - seekOffset;
  const curvePercentage = curveDuration / fadeDuration;
  const appliedCurveLength = Math.max(
    curvePercentage * FADE_IN_CURVE.length,
    2
  );
  const appliedCurve = FADE_IN_CURVE.slice(
    FADE_IN_CURVE.length - appliedCurveLength,
    FADE_IN_CURVE.length
  );
  const curveStart = Math.max(when, secondsToMilliseconds(context.currentTime));

  const fadeIn = context.createGain();
  fadeIn.gain.setValueCurveAtTime(
    appliedCurve,
    millisecondsToSeconds(curveStart),
    millisecondsToSeconds(curveDuration)
  );

  return fadeIn;
}

function buildFadeOutGain(
  context: BaseAudioContext,
  when: number,
  duration: number,
  fadeDuration: number
): AudioNode {
  const curveDuration = Math.min(duration, fadeDuration);
  const curvePercentage = curveDuration / fadeDuration;
  const appliedCurveLength = Math.max(
    curvePercentage * FADE_OUT_CURVE.length,
    2
  );
  const appliedCurve = FADE_OUT_CURVE.slice(
    FADE_OUT_CURVE.length - appliedCurveLength,
    FADE_OUT_CURVE.length
  );
  const fadeOut = context.createGain();
  const curveStart = Math.max(
    when + duration - curveDuration,
    secondsToMilliseconds(context.currentTime)
  );

  fadeOut.gain.setValueCurveAtTime(
    appliedCurve,
    millisecondsToSeconds(curveStart),
    millisecondsToSeconds(curveDuration)
  );

  return fadeOut;
}
